/**
 * LEITURA DE CÓDIGO DE BARRAS PELA CÂMERA — o motor, sem React.
 *
 * A tela (CameraScanner.tsx) só desenha; toda a mecânica de câmera está aqui,
 * porque é a parte que dá errado no aparelho de verdade e precisa ser lida sem
 * o barulho do JSX em volta.
 *
 * Decisões que valem explicação:
 *
 *  • O LEITOR É CARREGADO SÓ QUANDO ALGUÉM ABRE A CÂMERA. São ~1 MB (o motor de
 *    decodificação é WebAssembly). Quem entrou para ver a lista de remessas não
 *    paga esse download.
 *  • O ARQUIVO VEM DO PRÓPRIO SISTEMA, nunca de um endereço na internet. A
 *    biblioteca, se deixada por conta dela, busca o motor num CDN — o que
 *    funciona no escritório com internet e falha no galpão. Por isso o
 *    `locateFile` abaixo aponta para /scanner/… e é OBRIGATÓRIO.
 *  • LÊ SÓ O MEIO DO QUADRO. A câmera enxerga o frasco vizinho; se o leitor
 *    olhar a imagem inteira, mais cedo ou mais tarde ele confere o item errado
 *    sem ninguém perceber — que é o pior defeito possível numa conferência.
 *    Então recortamos uma faixa central (a mira que aparece na tela) e é só ela
 *    que vai para o decodificador.
 *  • ZERO DEPENDÊNCIA NO BUNDLE. O `import()` usa um endereço guardado em
 *    variável, de propósito: assim o empacotador não tenta resolver a
 *    biblioteca na hora do build. Ver server/assets/scanner/.
 */

/** Um código lido. */
export interface Achado {
  texto: string;
  formato: string;
}

/**
 * Tipos de etiqueta habilitados na leitura.
 *
 * Como as etiquetas vêm do CLIENTE, cada um imprime de um jeito — por isso a
 * lista é larga. Se um dia se souber que só chega um tipo (Code 128, digamos),
 * ENCURTAR ESTA LISTA deixa a leitura mais rápida e diminui a chance de ler um
 * código parecido por engano. É o único lugar a mexer para isso.
 *
 * Todos foram conferidos gerando e lendo de volta antes de a tela existir.
 */
export const FORMATOS: string[] = [
  "code_128", // etiqueta industrial (letras + números) — o mais comum
  "code_39", // antigo, ainda aparece em laboratório
  "code_93",
  "codabar", // laboratório clínico / banco de sangue
  "itf", // caixa e transporte
  "ean_13", // produto de prateleira
  "ean_8",
  "upc_a",
  "upc_e",
  "qr_code", // etiqueta impressa pelo próprio laboratório
  "data_matrix", // etiqueta minúscula, cabe em frasco pequeno
  "pdf417",
  "aztec",
];

/** Endereços dos arquivos servidos pelo próprio Laboratório (ver server/index.ts). */
const URL_BIBLIOTECA = "/scanner/scanner.js";
const URL_MOTOR = "/scanner/zxing_reader-3.1.3.wasm";

interface DetectorLike {
  detect(fonte: CanvasImageSource | Blob | ImageData): Promise<{ rawValue: string; format: string }[]>;
}

let detectorCarregado: DetectorLike | null = null;

/**
 * Carrega a biblioteca de leitura (uma vez por sessão) e devolve o detector.
 *
 * Se o arquivo não estiver no servidor (deploy incompleto), lança com uma
 * mensagem que a tela mostra como está — melhor dizer "o leitor não está
 * instalado no servidor" do que ficar girando para sempre.
 */
export async function carregarDetector(): Promise<DetectorLike> {
  if (detectorCarregado) return detectorCarregado;

  // Endereço em variável: é isso que mantém a biblioteca fora do bundle.
  const endereco = URL_BIBLIOTECA;
  let mod: any;
  try {
    mod = await import(/* @vite-ignore */ endereco);
  } catch {
    throw new Error(
      "Não foi possível carregar o leitor de código de barras deste servidor. " +
        "Use o leitor de mão e avise o suporte (arquivo /scanner/scanner.js).",
    );
  }

  // O PASSO QUE NÃO PODE FALTAR: apontar o motor para o arquivo local. Sem
  // isto a biblioteca busca num CDN — funciona no escritório e falha onde não
  // há internet, que é justamente a bancada.
  mod.prepareZXingModule({
    overrides: { locateFile: () => URL_MOTOR },
    fireImmediately: true,
  });

  detectorCarregado = new mod.BarcodeDetector({ formats: FORMATOS }) as DetectorLike;
  return detectorCarregado;
}

/** A câmera aberta, com o que dá para fazer com ela neste aparelho. */
export interface CameraAberta {
  stream: MediaStream;
  temLanterna: boolean;
  ligarLanterna(ligada: boolean): Promise<void>;
  fechar(): void;
}

/**
 * Por que a câmera não está disponível — ou `null` quando está tudo certo.
 *
 * O caso mais comum na prática NÃO é permissão negada: é abrir o sistema por um
 * endereço sem cadeado (http://192.168…). O navegador simplesmente não oferece
 * câmera fora de endereço seguro, e o erro que ele dá não explica isso.
 */
export function motivoSemCamera(): string | null {
  if (typeof window === "undefined") return "Câmera indisponível.";
  if (!window.isSecureContext) {
    return (
      "A câmera só funciona quando o sistema é aberto pelo endereço seguro " +
      "(https://laboratorio.portalsercamp.com). Neste endereço, use o leitor de mão."
    );
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return "Este navegador não oferece câmera. Use o leitor de mão ou abra pelo Chrome/Safari.";
  }
  return null;
}

/** Traduz os erros da câmera para algo que a pessoa da bancada resolve sozinha. */
function explicarErroCamera(err: unknown): string {
  const nome = (err as { name?: string })?.name ?? "";
  if (nome === "NotAllowedError" || nome === "SecurityError") {
    return "Permissão de câmera negada. Toque no cadeado ao lado do endereço e permita a câmera.";
  }
  if (nome === "NotFoundError" || nome === "OverconstrainedError") {
    return "Nenhuma câmera encontrada neste aparelho.";
  }
  if (nome === "NotReadableError") {
    return "A câmera está ocupada por outro aplicativo. Feche o aplicativo de câmera e tente de novo.";
  }
  return "Não foi possível abrir a câmera. Use o leitor de mão.";
}

/**
 * Abre a câmera TRASEIRA e prende no elemento de vídeo.
 *
 * `ideal` e não `exact` de propósito: com `exact`, um notebook sem câmera
 * traseira recusa a chamada inteira em vez de usar a que tem.
 */
export async function abrirCamera(video: HTMLVideoElement): Promise<CameraAberta> {
  const impedimento = motivoSemCamera();
  if (impedimento) throw new Error(impedimento);

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: "environment" },
        // Resolução alta ajuda em código de barras pequeno; é pedido, não exigido.
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    });
  } catch (err) {
    throw new Error(explicarErroCamera(err));
  }

  video.srcObject = stream;
  video.setAttribute("playsinline", "true"); // sem isto o iPhone abre em tela cheia
  video.muted = true;
  try {
    await video.play();
  } catch {
    /* alguns navegadores só deixam tocar depois de um toque — o vídeo aparece assim mesmo */
  }

  const track = stream.getVideoTracks()[0];
  const capacidades: any = track?.getCapabilities?.() ?? {};

  // Foco contínuo e um zoom leve ajudam MUITO em etiqueta pequena — mas só
  // existem no Android. Aplicar em bloco separado: se um deles não existir, o
  // navegador recusa a chamada inteira e perderíamos os dois.
  if (capacidades.focusMode?.includes?.("continuous")) {
    await track.applyConstraints({ advanced: [{ focusMode: "continuous" } as any] }).catch(() => {});
  }

  return {
    stream,
    temLanterna: !!capacidades.torch,
    async ligarLanterna(ligada: boolean) {
      await track?.applyConstraints({ advanced: [{ torch: ligada } as any] }).catch(() => {});
    },
    fechar() {
      for (const t of stream.getTracks()) t.stop();
      if (video.srcObject) video.srcObject = null;
    },
  };
}

/** Proporção da mira: faixa central larga e baixa, do jeito que um código 1D é. */
export const MIRA = { largura: 0.8, altura: 0.34 };

/**
 * Recorta a faixa central do vídeo (a mira). É o que vai para o decodificador.
 *
 * `createImageBitmap` é o caminho rápido; onde ele não existir (Safari antigo),
 * cai no canvas — mais lento, mas funciona.
 */
async function recortarMira(video: HTMLVideoElement, canvas: HTMLCanvasElement) {
  const largura = video.videoWidth;
  const altura = video.videoHeight;
  if (!largura || !altura) return null;

  const cw = Math.round(largura * MIRA.largura);
  const ch = Math.round(altura * MIRA.altura);
  const cx = Math.round((largura - cw) / 2);
  const cy = Math.round((altura - ch) / 2);

  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(video, cx, cy, cw, ch);
    } catch {
      /* cai no canvas abaixo */
    }
  }
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(video, cx, cy, cw, ch, 0, 0, cw, ch);
  return ctx.getImageData(0, 0, cw, ch);
}

/**
 * Fica lendo o vídeo até alguém mandar parar. Devolve a função de parar.
 *
 * Uma leitura por vez (`ocupado`): o decodificador é pesado e enfileirar
 * quadros só esquenta o aparelho e atrasa a resposta. ~120 ms entre tentativas
 * é o intervalo em que a leitura parece instantânea sem fritar a bateria.
 */
export function iniciarLeitura(
  video: HTMLVideoElement,
  detector: DetectorLike,
  aoLer: (achado: Achado) => void,
): () => void {
  const canvas = document.createElement("canvas");
  let parado = false;
  let ocupado = false;

  const tick = async () => {
    if (parado || ocupado) return;
    ocupado = true;
    try {
      const recorte = await recortarMira(video, canvas);
      if (recorte) {
        const achados = await detector.detect(recorte as any);
        if ("close" in (recorte as any)) (recorte as ImageBitmap).close();
        const primeiro = achados?.[0];
        if (primeiro?.rawValue) {
          aoLer({ texto: primeiro.rawValue, formato: String(primeiro.format ?? "") });
        }
      }
    } catch {
      /* quadro ruim (mexeu a mão, escuro) não é erro: o próximo resolve */
    } finally {
      ocupado = false;
    }
  };

  const timer = setInterval(tick, 120);
  return () => {
    parado = true;
    clearInterval(timer);
  };
}

/**
 * Sons da conferência.
 *
 * Som é o retorno certo aqui: a pessoa está olhando o frasco, não a tela. Três
 * sons distinguíveis sem precisar pensar — agudo curto = entrou; dois toques
 * médios = já tinha; grave longo = recusado.
 *
 * O AudioContext PRECISA nascer dentro do toque que abre a câmera, senão o
 * iPhone bloqueia o áudio e a conferência roda muda.
 */
export function criarSons() {
  let ctx: AudioContext | null = null;
  try {
    const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;
    ctx = Ctor ? new Ctor() : null;
    ctx?.resume?.();
  } catch {
    ctx = null;
  }

  const tocar = (frequencia: number, duracao: number, atraso = 0) => {
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const vol = ctx.createGain();
    osc.frequency.value = frequencia;
    osc.type = "sine";
    vol.gain.value = 0.14;
    osc.connect(vol).connect(ctx.destination);
    const inicio = ctx.currentTime + atraso;
    osc.start(inicio);
    osc.stop(inicio + duracao);
  };

  return {
    novo: () => tocar(1180, 0.09),
    duplicado: () => {
      tocar(760, 0.07);
      tocar(760, 0.07, 0.12);
    },
    recusado: () => tocar(220, 0.3),
    fechar: () => ctx?.close?.().catch?.(() => {}),
  };
}

/**
 * Mesma normalização que o servidor usa como chave (LOWER(TRIM)).
 *
 * Tem de ser idêntica, senão a trava local diverge do banco: a tela deixaria
 * passar um código que o banco recusa (ou o contrário).
 */
export const chaveDoCodigo = (codigo: string): string =>
  codigo.replace(/[\r\n\t]/g, "").trim().toLowerCase();
